import { VRM, VRMLookAt } from '@pixiv/three-vrm';
import { VRMAnimation, VRMAnimationLoaderPlugin, createVRMAnimationClip, VRMLookAtQuaternionProxy } from '@pixiv/three-vrm-animation';
import { GLTFLoader, GLTF } from 'three/examples/jsm/loaders/GLTFLoader.js';

/**
 * Load VRMA animation file and bind to VRM model
 * @param animationPath Path to VRMA file
 * @param vrm Target VRM model
 * @returns Loaded VRMAnimation
 */
export async function loadVRMAnimation(
  animationPath: string,
  vrm: VRM
): Promise<VRMAnimation | null> {
  try {
    // Create a GLTF loader with VRM animation plugin
    const loader = new GLTFLoader();
    loader.register((parser) => new VRMAnimationLoaderPlugin(parser));
    
    // Load the VRMA file
    const gltf = await loader.loadAsync(animationPath);

    // Try to retrieve animations via VRMAnimationLoaderPlugin helper
    let vrmAnimations: VRMAnimation[] | undefined;
    try {
      // Newer versions expose parse API
      interface VRMAnimationPluginStatic {
        parse?: (gltf: GLTF) => { vrmAnimations?: VRMAnimation[] };
      }
      const parsed = (VRMAnimationLoaderPlugin as unknown as VRMAnimationPluginStatic).parse?.(gltf);
      if (parsed?.vrmAnimations?.length) {
        vrmAnimations = parsed.vrmAnimations as VRMAnimation[];
      }
    } catch (_) {
      // ignore
    }

    // Fallbacks
    if (!vrmAnimations || vrmAnimations.length === 0) {
      interface UserDataWithAnimation {
        vrmAnimations?: VRMAnimation[];
        vrmAnimation?: VRMAnimation;
      }
      const userData = gltf.userData as UserDataWithAnimation;
      if (Array.isArray(userData.vrmAnimations)) {
        vrmAnimations = userData.vrmAnimations;
      } else if (userData.vrmAnimation) {
        vrmAnimations = [userData.vrmAnimation];
      }
    }

    if (!vrmAnimations || vrmAnimations.length === 0) {
      console.error(`No VRM animations found in: ${animationPath}`);
      return null;
    }

    const vrmAnimation: VRMAnimation = vrmAnimations[0]!; // use first animation (non-null)

    if (!vrmAnimation) {
      console.error(`No VRM animation found in first index of: ${animationPath}`);
      return null;
    }

    // Ensure VRMLookAtQuaternionProxy exists before creating animation clip
    interface VRMLookAtWithProxy extends VRMLookAt {
      quaternionProxy?: VRMLookAtQuaternionProxy;
    }
    const lookAtWithProxy = vrm.lookAt as VRMLookAtWithProxy | undefined;
    if (lookAtWithProxy && !lookAtWithProxy.quaternionProxy) {
      lookAtWithProxy.quaternionProxy = new VRMLookAtQuaternionProxy(lookAtWithProxy);
    }

    // Create animation clip from the VRM animation for validation/debug
    // Suppress console warnings in production
    const originalWarn = console.warn;
    if (process.env.NODE_ENV === 'production') {
      console.warn = () => {};
    }
    
    const animationClip = createVRMAnimationClip(vrmAnimation, vrm);
    
    // Restore console.warn
    if (process.env.NODE_ENV === 'production') {
      console.warn = originalWarn;
    }
    
    // Only log in development mode
    if (process.env.NODE_ENV === 'development' && animationClip) {
      console.warn(
        `VRMAnimation loaded: tracks=${animationClip.tracks.length}, duration=${animationClip.duration}`
      );
    }

    return vrmAnimation;
  } catch (error) {
    console.error(`Error loading VRM animation from ${animationPath}:`, error);
    return null;
  }
}

/**
 * Helper to get animation duration
 * @param animation VRM Animation
 * @returns Duration in seconds
 */
export function getAnimationDuration(animation: VRMAnimation): number {
  return animation.duration || 0;
}

/**
 * Get list of available bone names in animation
 * @param animation VRM Animation
 * @returns Array of bone names
 */
export function getAnimationBones(animation: VRMAnimation): string[] {
  if (!animation.humanoidTracks) {
    return [];
  }
  return Object.keys(animation.humanoidTracks);
}